import { motion } from 'framer-motion';
import { useNavigate, Link } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { Leaf, Award, Grape, Wine, Droplet, UtensilsCrossed } from 'lucide-react';
import { heroImage } from '@/lib/images';
import { useState, useEffect } from 'react';
import { settingsStore, categoriesStore } from '@/stores/localStorageStore';

const Hero = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [settings, setSettings] = useState(settingsStore.get());

  useEffect(() => {
    // Initial fetch
    setSettings(settingsStore.get());

    // Subscribe to changes
    const unsubscribe = settingsStore.subscribe((newSettings) => {
      setSettings(newSettings);
    });

    return () => unsubscribe();
  }, []);

  const features = [
    { icon: Grape, key: 'naturalWines' },
    { icon: Award, key: 'certified' },
  ];

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const navigateToGifts = () => {
    const categories = categoriesStore.getAll();
    const giftCategory = categories.find(c =>
      c.name.toLowerCase().includes('gift') ||
      c.name.toLowerCase().includes('prezent') ||
      c.name.toLowerCase().includes('zestaw') ||
      c.displayName.toLowerCase().includes('gift') ||
      c.displayName.toLowerCase().includes('prezent') ||
      c.displayName.toLowerCase().includes('zestaw')
    );

    if (giftCategory) {
      navigate(`/produkty?category=${giftCategory.name}`);
    } else {
      // Fallback if no matching category found
      navigate('/produkty?category=gifts');
    }
  };

  const hotspots = [
    {
      id: 'wine',
      titleKey: 'hotspotWineTitle',
      descKey: 'hotspotWineDesc',
      icon: Wine,
      path: '/produkty/wina',
      position: 'top-[56%] left-[32.5%]',
    },
    {
      id: 'oil',
      titleKey: 'hotspotOilTitle',
      descKey: 'hotspotOilDesc',
      icon: Droplet,
      path: '/produkty/oliwa',
      position: 'top-[67%] left-[75%]',
    },
    {
      id: 'snacks',
      titleKey: 'hotspotSnacksTitle',
      descKey: 'hotspotSnacksDesc',
      icon: UtensilsCrossed,
      path: '/produkty/tradycyjne-przekaski',
      position: 'top-[85%] left-[58%]',
    }
  ];

  return (
    <section className="relative min-h-[85vh] md:min-h-screen overflow-hidden">
      {/* Background Container - stays in background behind content */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Parallax Background - original brightness without dimming */}
        <div className="w-full h-full bg-[#1a0f0f] pointer-events-auto relative">
            <img 
              src={heroImage} 
              alt="Włoska Piwnica" 
              width="1920"
              height="1080"
              className="w-full h-full object-cover"
              loading="eager"
              decoding="async"
              fetchPriority="high"
            />
        </div>
      </div>

      {/* Hotspots Container - placed in front of content (z-20) to capture hover/click events */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none z-20">
        {/* Hotspots Overlay - locked to the background image dimensions and in front of text */}
        <div className="absolute inset-0 z-20 pointer-events-none hidden xl:block">
          {hotspots.map((hotspot) => {
            const Icon = hotspot.icon;
            return (
              <div
                key={hotspot.id}
                className={`absolute ${hotspot.position} pointer-events-auto group -translate-x-1/2 -translate-y-1/2`}
              >
                {/* Pulsating outer ring - slowed down to 3s for a premium feel */}
                <div 
                  className="absolute -inset-3 rounded-full bg-accent/30 animate-ping opacity-75 group-hover:bg-accent/50 group-hover:scale-125 transition-all duration-300"
                  style={{ animationDuration: '3s' }}
                />
                
                {/* Inner clickable link */}
                <Link
                  to={hotspot.path}
                  aria-label={t(hotspot.titleKey)}
                  className="relative flex items-center justify-center h-10 w-10 rounded-full bg-black/60 border border-white/20 text-white backdrop-blur-md transition-all duration-300 group-hover:scale-110 group-hover:bg-accent group-hover:border-accent group-hover:text-primary group-hover:shadow-[0_0_25px_rgba(212,175,55,0.8)]"
                >
                  <Icon className="h-5 w-5 transition-transform duration-300 group-hover:rotate-12" />
                </Link>

                {/* Tooltip Card - appears on hover */}
                <div className="absolute bottom-full mb-3 left-1/2 -translate-x-1/2 w-48 p-3 rounded-xl bg-black/85 border border-white/10 backdrop-blur-md shadow-2xl opacity-0 translate-y-2 pointer-events-none transition-all duration-300 ease-out group-hover:opacity-100 group-hover:translate-y-0 group-hover:pointer-events-auto">
                  <div className="text-center">
                    <span className="block font-serif text-accent text-xs font-bold uppercase tracking-wider mb-0.5">
                      {t(hotspot.titleKey)}
                    </span>
                    <p className="text-[10px] text-white/70 tracking-wide font-sans leading-tight">
                      {t(hotspot.descKey)}
                    </p>
                    <div className="mt-1.5 inline-flex items-center gap-1 text-[9px] text-accent/90 uppercase tracking-widest font-semibold font-serif group-hover:text-white transition-colors">
                      <span>{t('discoverMore') || 'Odkryj'}</span>
                      <span className="text-[10px]">→</span>
                    </div>
                  </div>
                  {/* Arrow down */}
                  <div className="absolute top-full left-1/2 -translate-x-1/2 border-x-4 border-x-transparent border-t-4 border-t-black/85" />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Decorative vine pattern */}
      <div
        className="absolute inset-0 opacity-5"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M50 0C55 20 70 30 50 50C30 70 45 100 50 100' stroke='%23ffffff' fill='none' stroke-width='0.5'/%3E%3Ccircle cx='50' cy='25' r='5' fill='%23ffffff'/%3E%3Ccircle cx='35' cy='60' r='3' fill='%23ffffff'/%3E%3Ccircle cx='65' cy='75' r='4' fill='%23ffffff'/%3E%3C/svg%3E")`,
        }}
      />

      {/* Logo - absolute top-left only on large desktops (xl+) */}
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.2, duration: 0.8 }}
        className="hidden xl:block absolute top-24 left-8 z-30"
      >
        <img
          src="/ui/logo/logo-winery.webp"
          alt="Włoska Piwnica Logo"
          width="600"
          height="200"
          className="xl:h-44 w-auto object-contain drop-shadow-[0_4px_10px_rgba(0,0,0,0.5)]"
        />
      </motion.div>

      <div className="container relative z-10 mx-auto flex min-h-[85vh] md:min-h-screen flex-col items-center justify-start px-4 pt-28 md:pt-40 lg:pt-48 pb-16 md:pb-32 lg:pb-48 text-center">

        {/* Logo - centered in flow on mobile & tablets, hidden on xl+ (where absolute logo shows) */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.2, duration: 0.8 }}
          className="xl:hidden flex justify-center mb-4 mt-2"
        >
          <img
            src="/ui/logo/logo-winery.webp"
            alt="Włoska Piwnica Logo"
            width="600"
            height="200"
            className="h-36 sm:h-40 md:h-48 lg:h-56 w-auto object-contain drop-shadow-[0_4px_10px_rgba(0,0,0,0.5)]"
          />
        </motion.div>

        {/* Main heading - hidden on tablet/mobile, visible from xl (desktops) */}
        <motion.h1
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.8 }}
          className="sr-only xl:not-sr-only xl:block mb-4 max-w-5xl mx-auto text-center select-none"
        >
          {(() => {
            const text = settings.hero_title || t('heroTitle');
            const titleText = text.toLowerCase() === 'włoska piwnica' ? 'Włoska Piwnica' : text;
            return (
              <span 
                className="hero-title-wloska-piwnica text-4xl md:text-[4rem] lg:text-[5.5rem] xl:text-[7rem] 2xl:text-[7.92rem] font-black tracking-normal"
                data-text={titleText}
              >
                {titleText}
              </span>
            );
          })()}
        </motion.h1>

        {/* Decorative line - hidden on mobile/tablet */}
        <motion.div
          initial={{ scaleX: 0 }}
          animate={{ scaleX: 1 }}
          transition={{ delay: 0.5, duration: 0.8 }}
          className="hidden xl:block mb-4 h-0.5 w-32 mx-auto bg-gradient-to-r from-transparent via-accent to-transparent"
        />

        {/* Subtitle */}
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="mb-6 max-w-2xl mx-auto font-serif text-base sm:text-lg text-primary-foreground/80 md:text-xl lg:text-2xl"
        >
          {settings.hero_subtitle !== undefined && settings.hero_subtitle !== null ? settings.hero_subtitle : t('heroSubtitle')}
        </motion.p>

        {/* Feature badges - compact pills moved below title, scaled by 30% */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.7 }}
          className="flex flex-wrap items-center justify-center gap-2 sm:gap-3 md:text-lg mt-2"
        >
          {features.map((feature, index) => (
            <motion.div
              key={feature.key}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8 + index * 0.1 }}
              className="flex items-center gap-1.5 rounded-full border border-primary-foreground/15 bg-black/30 px-3 py-1.5 backdrop-blur-sm"
            >
              <feature.icon className="h-3.5 w-3.5 text-accent" />
              <span className="text-[11px] font-medium tracking-wide text-primary-foreground/90 uppercase">
                {t(feature.key)}
              </span>
            </motion.div>
          ))}
        </motion.div>

        {/* Order Now Button — 12% smaller on mobile */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9 }}
          className="mt-6 md:mt-8 w-full md:w-auto px-4 md:px-0"
        >
          <motion.div
            whileHover={{ scale: 1.05, boxShadow: '0 20px 40px -10px rgba(0,0,0,0.3)' }}
            whileTap={{ scale: 0.95 }}
            className="inline-block w-full max-w-xs md:max-w-none md:w-auto"
          >
            <Link
              to="/produkty"
              className="group relative overflow-hidden rounded-full bg-primary-foreground flex justify-center w-full px-8 sm:px-14 md:px-24 py-3.5 md:py-4 text-xs md:text-sm font-semibold uppercase tracking-widest text-primary transition-all"
            >
              <span className="relative z-10">{t('orderNow')}</span>
              <motion.div
                className="absolute inset-0 bg-accent"
                initial={{ x: '-100%' }}
                whileHover={{ x: 0 }}
                transition={{ duration: 0.3 }}
              />
            </Link>
          </motion.div>
        </motion.div>
      </div>


      {/* Scroll indicator */}


    </section >
  );
};

export default Hero;
